"use client";
import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import Fuse from "fuse.js";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { fetchAllowedDealers, fetchAllowedProducts, getDealerContractedProducts, type Dealer } from "@/modules/dealer";
import { useDashboard } from "@/modules/dashboard/context/DashboardContext";
import { useDashboardActions } from "@/modules/dashboard/hooks/useDashboardActions";
import { usePage } from "@/modules/pages";
import { useAuth } from "@/modules/auth";
import { canViewPage } from "@/modules/pages/utils/permissions";
import { isDisabledByProducts } from "@/modules/pages/utils/products";
import { useNavigate } from "@/hooks/useNavigate";
import { STATUS_API_TO_DISPLAY, DealerFilters as DealerFiltersType, DealerFilterKey, DealerFilterPermissionsConfig } from "./types";
import { canViewDealerFilter } from "./permissions";
import { fetchDealerFilterPermissions, fetchRemovedOems } from "@/modules/config";
import { DropdownArrow } from "@/components/icons/DropdownArrow";
import { DealerSearchInput } from "./DealerSearchInput";
import { DealerResults } from "./DealerResults";
import { DealerFilters } from "./DealerFilters";
import { getStateAbbr } from "@/constants/states";

export function DealerSelector() {
    const { state } = useDashboard();
    const { setSelectedDealer } = useDashboardActions();
    const { selectedDealer, hydrated } = state;
    const { user, accessToken } = useAuth();
    const { state: pageState } = usePage();
    const navigate = useNavigate();
    const [isExpanded, setIsExpanded] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [dealers, setDealers] = useState<Dealer[]>([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState<string[]>(["Active"]);
    const [dealerFilters, setDealerFilters] = useState<DealerFiltersType>({
        oem: [],
        product: [],
        group: [],
        program: [],
        state: [],
    });
    const [error, setError] = useState<string | null>(null);
    const [highlightIndex, setHighlightIndex] = useState<number>(-1);
    const [isMounted, setIsMounted] = useState(false);
    const [permissionsMap, setPermissionsMap] = useState<DealerFilterPermissionsConfig | null>(null);
    const [allowedProducts, setAllowedProducts] = useState<string[]>([]);
    const [removedOems, setRemovedOems] = useState<string[]>([]);
    const currentProductNamesRef = useRef<string | undefined>(undefined);

    const allowedProductsFetchedRef = useRef(false);
    useEffect(() => {
        if (allowedProductsFetchedRef.current) return;
        allowedProductsFetchedRef.current = true;
        fetchAllowedProducts()
            .then(setAllowedProducts)
            .catch((err) => {
                console.error("Failed to fetch allowed products:", err);
            });
    }, []);

    const containerRef = useRef<HTMLDivElement>(null);
    const collapsedRef = useRef<HTMLDivElement>(null);
    const autoOpenedRef = useRef(false);
    const autoSelectedRef = useRef(false);
    const [collapsedPosition, setCollapsedPosition] = useState({ top: 0, left: 0, width: 0 });

    useEffect(() => {
        setIsMounted(true);
    }, []);

    const permissionsFetchedRef = useRef(false);
    useEffect(() => {
        if (permissionsFetchedRef.current) return;
        permissionsFetchedRef.current = true;
        fetchDealerFilterPermissions()
            .then(setPermissionsMap)
            .catch((err) => {
                console.error("Failed to fetch dealer filter permissions:", err);
            });
    }, []);

    const removedOemsFetchedRef = useRef(false);
    useEffect(() => {
        if (removedOemsFetchedRef.current) return;
        removedOemsFetchedRef.current = true;
        fetchRemovedOems()
            .then(setRemovedOems)
            .catch((err) => {
                console.error("Failed to fetch removed OEMs:", err);
            });
    }, []);

    const apiProductFilter = useMemo<string[] | undefined>(() => {
        if (dealerFilters.product.length === 0 || dealerFilters.product.length === allowedProducts.length) {
            return undefined;
        }
        return dealerFilters.product;
    }, [dealerFilters.product, allowedProducts.length]);

    const loadDealers = useCallback(async (productNames: string[] | undefined) => {
        const key = JSON.stringify(productNames ?? null);
        currentProductNamesRef.current = key;
        setLoading(true);
        try {
            const data = await fetchAllowedDealers({
                date: new Date().toString(),
                productNames,
            });
            if (currentProductNamesRef.current !== key) return; // stale
            const mapped: Dealer[] = (data || []).map((dealer) => ({
                ...dealer,
                state: getStateAbbr(dealer.dealerState),
            }));
            setDealers(mapped);
            setError(null);
        } catch (e: any) {
            if (currentProductNamesRef.current !== key) return;
            console.error("Failed to fetch allowed dealers:", e);
            setDealers([]);
            setError("Failed to fetch allowed dealers");
        } finally {
            if (currentProductNamesRef.current === key) setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadDealers(apiProductFilter);
    }, [loadDealers, apiProductFilter]);

    useEffect(() => {
        if (!hydrated) return;
        if (autoSelectedRef.current) return;
        if (selectedDealer) return;
        if (dealers.length === 0) return;

        autoSelectedRef.current = true;
        setSelectedDealer(dealers[0]);
    }, [hydrated, selectedDealer, dealers, setSelectedDealer]);

    const handleExpand = () => {
        if (collapsedRef.current) {
            const rect = collapsedRef.current.getBoundingClientRect();
            setCollapsedPosition({
                top: rect.bottom + 16,
                left: rect.left - 4,
                width: rect.width,
            });
        }
        setIsExpanded(true);
    };

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsExpanded(false);
                if (!selectedDealer) setDismissed(true);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [selectedDealer]);

    useEffect(() => {
        if (!isExpanded) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                setIsExpanded(false);
                if (!selectedDealer) setDismissed(true);
                setHighlightIndex(-1);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [isExpanded, selectedDealer]);

    const filterOptions = useMemo<DealerFiltersType>(() => {
        const oems = new Set<string>();
        const groups = new Set<string>();
        const programs = new Set<string>();
        const states = new Set<string>();

        dealers.forEach((dealer) => {
            if (dealer.eomName && !removedOems.includes(dealer.eomName)) oems.add(dealer.eomName);
            if (dealer.dealerGroup) groups.add(dealer.dealerGroup);
            if (dealer.oemProgramName) programs.add(dealer.oemProgramName);
            if (dealer.state) states.add(dealer.state);
        });

        return {
            oem: Array.from(oems).sort(),
            product: allowedProducts,
            group: Array.from(groups).sort(),
            program: Array.from(programs).sort(),
            state: Array.from(states).sort(),
        };
    }, [dealers, allowedProducts, removedOems]);

    const handleFilterChange = useCallback((key: DealerFilterKey, values: string[]) => {
        setDealerFilters((prev) => ({ ...prev, [key]: values }));
    }, []);

    // Step 1: Apply status + dropdown filters (no search term dependency).
    // Product filter is server-side (via productNames query param), so it's not re-applied here.
    const prefilteredDealers = useMemo(() => {
        if (statusFilter.length === 0) return [];

        let result = [...dealers];

        if (!statusFilter.includes("All")) {
            result = result.filter((dealer) => {
                const displayStatus = STATUS_API_TO_DISPLAY[dealer.mapStatus];
                return !!displayStatus && statusFilter.includes(displayStatus);
            });
        }

        if (canViewDealerFilter("oem", user, permissionsMap) && dealerFilters.oem.length > 0) {
            result = result.filter((d) => d.eomName && dealerFilters.oem.includes(d.eomName));
        }
        if (canViewDealerFilter("group", user, permissionsMap) && dealerFilters.group.length > 0) {
            result = result.filter((d) => d.dealerGroup && dealerFilters.group.includes(d.dealerGroup));
        }
        if (canViewDealerFilter("program", user, permissionsMap) && dealerFilters.program.length > 0) {
            result = result.filter((d) => d.oemProgramName && dealerFilters.program.includes(d.oemProgramName));
        }
        if (canViewDealerFilter("state", user, permissionsMap) && dealerFilters.state.length > 0) {
            result = result.filter((d) => d.state && dealerFilters.state.includes(d.state));
        }

        return result;
    }, [dealers, statusFilter, dealerFilters, user, permissionsMap]);

    // Step 2: Build Fuse index only when the pre-filtered list changes (not on every keystroke)
    const fuseStandard = useMemo(
        () =>
            new Fuse(prefilteredDealers, {
                keys: ["dealerName"],
                threshold: 0.2,
                minMatchCharLength: 2,
                ignoreLocation: true,
            }),
        [prefilteredDealers],
    );

    const fuseExtended = useMemo(
        () =>
            new Fuse(prefilteredDealers, {
                keys: ["dealerName"],
                threshold: 0.2,
                minMatchCharLength: 2,
                ignoreLocation: true,
                useExtendedSearch: true,
            }),
        [prefilteredDealers],
    );

    // Step 3: Apply fuzzy search on the pre-built index
    const filteredDealers = useMemo(() => {
        const trimmed = searchTerm.trim();
        if (!trimmed) return prefilteredDealers;

        const tokens = trimmed.split(/\s+/).filter(Boolean);

        if (tokens.length === 1) {
            return fuseStandard.search(trimmed).map((r) => r.item);
        }

        const extendedQuery = {
            $and: tokens.map((token) => ({ dealerName: `'${token}` })),
        };

        return fuseExtended.search(extendedQuery).map((r) => r.item);
    }, [searchTerm, fuseStandard, fuseExtended, prefilteredDealers]);

    // Dynamic font size based on dealer name length and screen size
    const getDealerNameFontSize = useCallback((dealerName: string | null | undefined, screenWidth?: number) => {
        if (!dealerName) return "text-[44px]";

        const nameLength = dealerName.length;
        const isSmallScreen = screenWidth && screenWidth < 768;
        const isMediumScreen = screenWidth && screenWidth >= 768 && screenWidth < 1024;

        const baseSize = isSmallScreen ? "text-[22px]" : isMediumScreen ? "text-[34px]" : "text-[44px]";

        if (nameLength > 35) {
            return isSmallScreen ? "text-[16px]" : isMediumScreen ? "text-[22px]" : "text-[26px]";
        } else if (nameLength > 25) {
            return isSmallScreen ? "text-[18px]" : isMediumScreen ? "text-[26px]" : "text-[34px]";
        } else {
            return baseSize;
        }
    }, []);

    const [screenWidth, setScreenWidth] = useState(0);
    const dealerNameFontSize = getDealerNameFontSize(selectedDealer?.dealerName, screenWidth);

    useEffect(() => {
        const handleResize = () => {
            setScreenWidth(window.innerWidth);
        };

        handleResize();

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const handleSelect = useCallback(async (dealer: Dealer) => {
        setSelectedDealer(dealer);
        setSearchTerm("");
        setIsExpanded(false);
        setHighlightIndex(-1);
        setDismissed(false);

        // Fetch the selected dealer's products on demand to drive page-permission navigation.
        const contracted = await getDealerContractedProducts(dealer.dealerRefId);
        const dealerProducts = (contracted?.contractedProducts || []).map((p) => p.toLowerCase());

        const visiblePages = pageState.pages.filter((p) => canViewPage(p, user));
        const firstEnabledPage = visiblePages.find(
            (p) => !isDisabledByProducts(p?.metadata?.products, dealerProducts),
        );

        if (firstEnabledPage) {
            navigate(`/dashboard/?pageId=${firstEnabledPage.id}`);
        }
    }, [setSelectedDealer, pageState.pages, user, navigate]);

    return (
        <>
            {hydrated &&
                <div
                    ref={collapsedRef}
                    className={`flex items-center justify-start gap-2 relative cursor-pointer min-w-0`}
                    onClick={handleExpand}
                >
                    <div className={`font-rajdhani ${dealerNameFontSize} text-white truncate`} style={{ fontWeight: '450' }} title={selectedDealer?.dealerName || undefined}>{selectedDealer?.dealerName || "Select Dealer"}</div>
                    <DropdownArrow color="#fff" />
                </div>
            }

            {isMounted && isExpanded && createPortal(
                <AnimatePresence>
                    <>
                        {/* Backdrop to capture outside clicks */}
                        <div
                            className="fixed inset-0 bg-black/20"
                            style={{ zIndex: 999 }}
                            onClick={() => {
                                setIsExpanded(false);
                                if (!selectedDealer) setDismissed(true);
                            }}
                        />

                        <motion.div
                            ref={containerRef}
                            initial={{
                                top: collapsedPosition.top,
                                left: collapsedPosition.left,
                                width: collapsedPosition.width,
                                height: 45,
                                opacity: 0.8,
                            }}
                            animate={{
                                top: collapsedPosition.top,
                                left: Math.min(collapsedPosition.left, window.innerWidth - Math.min(800, window.innerWidth - 32) - 16),
                                width: Math.min(800, window.innerWidth - 32),
                                height: "auto",
                                opacity: 1,
                            }}
                            exit={{
                                top: collapsedPosition.top,
                                left: collapsedPosition.left,
                                width: collapsedPosition.width,
                                height: 45,
                                opacity: 0,
                            }}
                            transition={{
                                type: "spring",
                                damping: 30,
                                stiffness: 200,
                                mass: 0.8,
                            }}
                            style={{ zIndex: 1000 }}
                            className="fixed bg-white rounded-md shadow-lg border border-gray-200"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.25 }} className="w-full">
                                <div className="flex flex-col gap-2.5 w-full px-4 py-4">
                                    {/* Main Content */}
                                    <div className="flex flex-col gap-5 w-full">
                                        <DealerSearchInput
                                            searchTerm={searchTerm}
                                            onSearchChange={setSearchTerm}
                                            statusFilter={statusFilter}
                                            onStatusChange={setStatusFilter}
                                            isExpanded={isExpanded}
                                            visibleDealers={filteredDealers}
                                            highlightIndex={highlightIndex}
                                            onHighlightChange={setHighlightIndex}
                                            onSelect={handleSelect}
                                        />
                                        <DealerFilters
                                            filters={dealerFilters}
                                            filterOptions={filterOptions}
                                            onFilterChange={handleFilterChange}
                                            permissionsMap={permissionsMap}
                                        />
                                        <p className="font-inter text-sm text-[#079a95]">Results found: {filteredDealers.length}</p>
                                    </div>

                                    {/* Dealer Results */}
                                    <DealerResults
                                        loading={loading}
                                        error={error}
                                        visibleDealers={filteredDealers}
                                        highlightIndex={highlightIndex}
                                        onHighlightChange={setHighlightIndex}
                                        onSelect={handleSelect}
                                    />
                                </div>
                            </motion.div>
                        </motion.div>
                    </>
                </AnimatePresence>,
                document.body
            )}
        </>
    );
}
